import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  Briefcase, Users, Handshake, Crown, ArrowLeft, Play, 
  MessageCircle, Send, CheckCircle, Target, Clock, Award,
  ChevronRight, Lightbulb, AlertCircle, TrendingUp, Star,
  Shield, Sparkles, Volume2, Mic
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const CATEGORY_ICONS = {
  client_mastery: Briefcase,
  candidate_mastery: Users,
  negotiation_mastery: Handshake,
  executive_communication: Crown,
  compliance_mastery: Shield,
  creative_outreach: Sparkles
};

// Category colors - explicit class mappings for Tailwind JIT
const CATEGORY_BG_CLASSES = {
  client_mastery: 'bg-blue-500/20',
  candidate_mastery: 'bg-green-500/20',
  negotiation_mastery: 'bg-purple-500/20',
  executive_communication: 'bg-amber-500/20',
  compliance_mastery: 'bg-red-500/20',
  creative_outreach: 'bg-pink-500/20'
};

const CATEGORY_TEXT_CLASSES = {
  client_mastery: 'text-blue-400',
  candidate_mastery: 'text-green-400',
  negotiation_mastery: 'text-purple-400',
  executive_communication: 'text-amber-400',
  compliance_mastery: 'text-red-400',
  creative_outreach: 'text-pink-400'
};

// Difficulty badges
const DifficultyBadge = ({ difficulty }) => {
  const colors = {
    easy: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    tough: 'bg-rose-500/20 text-rose-400 border-rose-500/30'
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full border ${colors[difficulty] || colors.medium}`}>
      {difficulty?.charAt(0).toUpperCase() + difficulty?.slice(1)}
    </span>
  );
};

// Score display component
const ScoreDisplay = ({ score, size = 'md' }) => {
  const getColor = (s) => {
    if (s >= 80) return 'text-emerald-400';
    if (s >= 60) return 'text-amber-400';
    return 'text-rose-400';
  };
  
  const sizeClasses = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-4xl'
  };
  
  return (
    <span className={`font-bold ${getColor(score)} ${sizeClasses[size]}`}>
      {Math.round(score)}%
    </span>
  );
};

// Role-Play Lab Main Component
const RolePlayLab = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commScore, setCommScore] = useState(null);

  useEffect(() => {
    fetchData();
    fetchCommScore();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, scenariosRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/roleplay/categories`, { withCredentials: true }),
        axios.get(`${API_URL}/api/roleplay/scenarios`, { withCredentials: true }),
        axios.get(`${API_URL}/api/roleplay/stats`, { withCredentials: true })
      ]);
      
      setCategories(categoriesRes.data.categories);
      setScenarios(scenariosRes.data.scenarios);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching role-play data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommScore = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/communication-score/module/role_play`, { withCredentials: true });
      setCommScore(res.data);
    } catch (error) {
      console.error('Error fetching communication score:', error);
    }
  };

  const filteredScenarios = selectedCategory 
    ? scenarios.filter(s => s.category === selectedCategory)
    : scenarios;

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(240,10%,4%)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(240,10%,4%)] text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/staffing-training')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Staffing Training
          </button>
          
          <h1 className="text-3xl font-bold mb-2">Role-Play Lab</h1>
          <p className="text-gray-400">
            Practice real recruitment conversations with AI personas. Master the Art of Speaking and Handling.
          </p>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="text-gray-400 text-sm mb-1">Sessions Completed</div>
              <div className="text-2xl font-bold">{stats.completed_sessions}</div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="text-gray-400 text-sm mb-1">Average Score</div>
              <ScoreDisplay score={stats.average_score} />
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="text-gray-400 text-sm mb-1">Scenarios Attempted</div>
              <div className="text-2xl font-bold">{stats.scenarios_attempted} / {stats.scenarios_available}</div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="text-gray-400 text-sm mb-1">Total Sessions</div>
              <div className="text-2xl font-bold">{stats.total_sessions}</div>
            </div>
          </div>
        )}

        {/* Communication Score Card */}
        {commScore?.data?.has_data && (
          <Link 
            to="/communication-profile"
            className="block mb-8 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-4 border border-purple-500/30 hover:border-purple-500/50 transition-all"
            data-testid="roleplay-comm-score"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-purple-500/20">
                  <Volume2 className="h-6 w-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Your Communication Score</p>
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl font-bold ${
                      (commScore.data.communication_score || 0) >= 80 ? 'text-emerald-400' :
                      (commScore.data.communication_score || 0) >= 60 ? 'text-amber-400' :
                      'text-rose-400'
                    }`}>
                      {Math.round(commScore.data.communication_score || commScore.data.overall_score || 0)}%
                    </span>
                    <span className="text-gray-500">|</span>
                    <span className="text-gray-400 text-sm">{commScore.data.sessions || 0} sessions</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-purple-400 text-sm">
                View Details
                <ChevronRight size={16} />
              </div>
            </div>
            {commScore.tips?.[0] && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-xs text-gray-400">
                  <Lightbulb size={12} className="inline mr-1 text-amber-400" />
                  {commScore.tips[0]}
                </p>
              </div>
            )}
          </Link>
        )}

        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-lg border transition-all ${
              !selectedCategory 
                ? 'bg-blue-500/20 border-blue-500 text-blue-400' 
                : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'
            }`}
          >
            All Scenarios
          </button>
          {categories.map(cat => {
            const Icon = CATEGORY_ICONS[cat.id] || Briefcase;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                  selectedCategory === cat.id 
                    ? 'bg-blue-500/20 border-blue-500 text-blue-400' 
                    : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'
                }`}
              >
                <Icon size={16} />
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* Scenarios Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredScenarios.map(scenario => {
            const Icon = CATEGORY_ICONS[scenario.category] || Briefcase;
            return (
              <div 
                key={scenario.id}
                className="bg-white/5 rounded-xl border border-white/10 overflow-hidden hover:border-white/20 transition-all group"
              >
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-lg ${CATEGORY_BG_CLASSES[scenario.category] || 'bg-blue-500/20'}`}>
                      <Icon size={20} className={CATEGORY_TEXT_CLASSES[scenario.category] || 'text-blue-400'} />
                    </div>
                    <DifficultyBadge difficulty={scenario.difficulty} />
                  </div>
                  
                  {/* Title & Description */}
                  <h3 className="text-lg font-semibold mb-2 group-hover:text-blue-400 transition-colors">
                    {scenario.name}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                    {scenario.description}
                  </p>
                  
                  {/* Persona */}
                  <div className="flex items-center gap-2 mb-4 text-sm">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-medium">
                      {scenario.persona_name?.charAt(0)}
                    </div>
                    <div>
                      <div className="text-white">{scenario.persona_name}</div>
                      <div className="text-gray-500 text-xs">{scenario.persona_title}</div>
                    </div>
                  </div>
                  
                  {/* Frameworks */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {scenario.frameworks?.map(fw => (
                      <span key={fw} className="px-2 py-1 bg-white/5 rounded text-xs text-gray-400">
                        {fw.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                  
                  {/* Stats & Action */}
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {scenario.duration_minutes}m
                      </span>
                      {scenario.completion_count > 0 && (
                        <span className="flex items-center gap-1">
                          <CheckCircle size={14} className="text-emerald-400" />
                          {scenario.completion_count}x
                        </span>
                      )}
                      {scenario.best_score && (
                        <span className="flex items-center gap-1">
                          <Star size={14} className="text-amber-400" />
                          {Math.round(scenario.best_score)}%
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => navigate(`/roleplay/${scenario.id}`)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 rounded-lg text-sm font-medium transition-colors"
                    >
                      <Play size={14} />
                      Start
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Role-Play Session Component
const RolePlaySession = () => {
  const navigate = useNavigate();
  const { scenarioId } = useParams();
  const [scenario, setScenario] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [speechAnalysis, setSpeechAnalysis] = useState(null);
  const [showTips, setShowTips] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchScenario();
  }, [scenarioId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchScenario = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/roleplay/scenarios/${scenarioId}`, { withCredentials: true });
      setScenario(res.data);
    } catch (error) {
      console.error('Error fetching scenario:', error);
    } finally {
      setLoading(false);
    }
  };

  const startSession = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${API_URL}/api/roleplay/sessions/start?scenario_id=${scenarioId}`,
        {},
        { withCredentials: true }
      );
      setSession(res.data);
      setMessages([{
        role: 'persona',
        content: res.data.opening_message,
        timestamp: new Date().toISOString()
      }]);
      setShowTips(false);
    } catch (error) {
      console.error('Error starting session:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);

    // Add user message immediately
    setMessages(prev => [...prev, {
      role: 'recruiter',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const res = await axios.post(
        `${API_URL}/api/roleplay/sessions/${session.session_id}/message?message=${encodeURIComponent(userMessage)}`,
        {},
        { withCredentials: true }
      );

      // Add persona response
      setMessages(prev => [...prev, {
        role: 'persona',
        content: res.data.persona_response,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const endSession = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${API_URL}/api/roleplay/sessions/${session.session_id}/end`,
        {},
        { withCredentials: true }
      );
      setEvaluation(res.data.evaluation);
      
      // Capture speech analysis if available
      if (res.data.speech_analysis) {
        setSpeechAnalysis(res.data.speech_analysis);
      }
    } catch (error) {
      console.error('Error ending session:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading && !scenario) {
    return (
      <div className="min-h-screen bg-[hsl(240,10%,4%)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Evaluation View
  if (evaluation) {
    return (
      <div className="min-h-screen bg-[hsl(240,10%,4%)] text-white p-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 mb-4">
              <Award size={40} />
            </div>
            <h1 className="text-2xl font-bold mb-2">Session Complete!</h1>
            <p className="text-gray-400">{scenario?.name}</p>
          </div>

          {/* Overall Score */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center mb-6">
            <div className="text-gray-400 mb-2">Overall Score</div>
            <ScoreDisplay score={evaluation.overall_score} size="lg" />
          </div>

          {/* Strengths & Improvements */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-emerald-500/10 rounded-xl p-5 border border-emerald-500/20">
              <h3 className="flex items-center gap-2 font-semibold text-emerald-400 mb-3">
                <CheckCircle size={18} />
                What You Did Well
              </h3>
              <ul className="space-y-2">
                {evaluation.strengths?.map((strength, i) => (
                  <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                    <span className="text-emerald-400 mt-0.5">•</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-amber-500/10 rounded-xl p-5 border border-amber-500/20">
              <h3 className="flex items-center gap-2 font-semibold text-amber-400 mb-3">
                <TrendingUp size={18} />
                Areas to Improve
              </h3>
              <ul className="space-y-2">
                {evaluation.improvements?.map((improvement, i) => (
                  <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                    <span className="text-amber-400 mt-0.5">•</span>
                    {improvement}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Coaching Tips */}
          {evaluation.coaching_tips && evaluation.coaching_tips.length > 0 && (
            <div className="bg-blue-500/10 rounded-xl p-5 border border-blue-500/20 mb-6">
              <h3 className="flex items-center gap-2 font-semibold text-blue-400 mb-3">
                <Lightbulb size={18} />
                Coaching Tips
              </h3>
              <ul className="space-y-2">
                {evaluation.coaching_tips.map((tip, i) => (
                  <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">{i + 1}.</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Category Scores */}
          {evaluation.category_scores && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/10 mb-6">
              <h3 className="font-semibold mb-4">Detailed Scores</h3>
              <div className="space-y-3">
                {Object.entries(evaluation.category_scores).map(([key, value]) => (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="text-white">{value.score}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          value.score >= 80 ? 'bg-emerald-500' : 
                          value.score >= 60 ? 'bg-amber-500' : 'bg-rose-500'
                        }`}
                        style={{ width: `${value.score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Speech Analysis - Voice Pattern Insights */}
          {speechAnalysis && (
            <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 rounded-xl p-5 border border-violet-500/30 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Mic size={20} className="text-violet-400" />
                <h3 className="font-semibold">Voice Pattern Analysis</h3>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-white/5 rounded-lg">
                  <div className={`text-2xl font-bold ${
                    speechAnalysis.clarity_score >= 80 ? 'text-emerald-400' :
                    speechAnalysis.clarity_score >= 60 ? 'text-amber-400' : 'text-rose-400'
                  }`}>
                    {Math.round(speechAnalysis.clarity_score)}
                  </div>
                  <div className="text-xs text-gray-400">Clarity Score</div>
                </div>
                <div className="text-center p-3 bg-white/5 rounded-lg">
                  <div className="text-2xl font-bold text-blue-400">
                    {speechAnalysis.filler_word_rate.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-400">Filler Rate</div>
                </div>
                <div className="text-center p-3 bg-white/5 rounded-lg">
                  <div className="text-2xl font-bold text-purple-400">
                    {Math.round(speechAnalysis.speaking_pace_wpm)}
                  </div>
                  <div className="text-xs text-gray-400">WPM</div>
                </div>
              </div>

              {speechAnalysis.strengths?.length > 0 && (
                <div className="mb-3">
                  <div className="text-sm text-gray-400 mb-2">Voice Strengths</div>
                  <div className="flex flex-wrap gap-2">
                    {speechAnalysis.strengths.map((s, i) => (
                      <span key={i} className="px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded text-xs">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {speechAnalysis.improvement_areas?.length > 0 && (
                <div>
                  <div className="text-sm text-gray-400 mb-2">Voice Focus Areas</div>
                  <div className="flex flex-wrap gap-2">
                    {speechAnalysis.improvement_areas.map((area, i) => (
                      <span key={i} className="px-2 py-1 bg-amber-500/20 text-amber-300 rounded text-xs">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-white/10">
                <a 
                  href="/speech-profile" 
                  className="text-sm text-violet-400 hover:text-violet-300 flex items-center gap-1"
                >
                  View full voice profile →
                </a>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={() => {
                setEvaluation(null);
                setSpeechAnalysis(null);
                setSession(null);
                setMessages([]);
                setShowTips(true);
              }}
              className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 rounded-xl font-medium transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/roleplay')}
              className="flex-1 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-colors"
            >
              Back to Lab
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(240,10%,4%)] text-white flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-white/10 bg-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/roleplay')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-semibold">{scenario?.name}</h1>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>{scenario?.persona?.name}</span>
                <span>•</span>
                <span>{scenario?.persona?.title}</span>
              </div>
            </div>
          </div>
          
          {session && messages.length >= 6 && (
            <button
              onClick={endSession}
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium transition-colors"
              disabled={loading}
            >
              {loading ? 'Evaluating...' : 'End & Get Feedback'}
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {!session ? (
          // Pre-session view
          <div className="max-w-2xl mx-auto p-6">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-6">
              <h2 className="text-xl font-semibold mb-4">Scenario Overview</h2>
              <p className="text-gray-400 mb-4">{scenario?.description}</p>
              
              {/* Learning Objectives */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-300 mb-2">Learning Objectives</h3>
                <ul className="space-y-1">
                  {scenario?.learning_objectives?.map((obj, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                      <Target size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
                      {obj}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Frameworks */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-300 mb-2">Frameworks to Practice</h3>
                <div className="flex flex-wrap gap-2">
                  {scenario?.frameworks?.map(fw => (
                    <span key={fw} className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-sm">
                      {fw.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>

              {/* Persona Info */}
              <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-lg font-bold">
                  {scenario?.persona?.name?.charAt(0)}
                </div>
                <div>
                  <div className="font-medium">{scenario?.persona?.name}</div>
                  <div className="text-sm text-gray-400">{scenario?.persona?.title} at {scenario?.persona?.company}</div>
                </div>
              </div>
            </div>

            <button
              onClick={startSession}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <Play size={20} />
                  Start Role-Play
                </>
              )}
            </button>
          </div>
        ) : (
          // Chat view
          <div className="h-full flex flex-col max-w-4xl mx-auto w-full">
            {/* Tips Banner */}
            {showTips && (
              <div className="p-4 bg-blue-500/10 border-b border-blue-500/20">
                <div className="flex items-start gap-3">
                  <Lightbulb size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-blue-400 mb-1">Tips for this scenario</div>
                    <ul className="text-xs text-gray-400 space-y-1">
                      {session.tips?.map((tip, i) => (
                        <li key={i}>• {tip}</li>
                      ))}
                    </ul>
                  </div>
                  <button onClick={() => setShowTips(false)} className="text-gray-500 hover:text-white">
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'recruiter' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] ${msg.role === 'recruiter' ? 'order-2' : ''}`}>
                    {msg.role === 'persona' && (
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-xs">
                          {scenario?.persona?.name?.charAt(0)}
                        </div>
                        <span className="text-xs text-gray-400">{scenario?.persona?.name}</span>
                      </div>
                    )}
                    <div className={`p-4 rounded-2xl ${
                      msg.role === 'recruiter' 
                        ? 'bg-blue-500 text-white rounded-br-sm' 
                        : 'bg-white/10 text-white rounded-bl-sm'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                </div>
              ))}
              
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white/10 p-4 rounded-2xl rounded-bl-sm">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/10 bg-white/5">
              <div className="flex gap-3">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your response..."
                  className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3 resize-none focus:outline-none focus:border-blue-500 transition-colors"
                  rows={2}
                  disabled={sending}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || sending}
                  className="px-4 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors"
                >
                  <Send size={20} />
                </button>
              </div>
              
              <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                <span>Press Enter to send, Shift+Enter for new line</span>
                <span>{messages.length} messages • {messages.length >= 6 ? 'Ready to evaluate' : `${6 - messages.length} more to evaluate`}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { RolePlayLab, RolePlaySession };
export default RolePlayLab;
